import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({ necessary: true, analytics: true, marketing: true }));
    setIsVisible(false);
    setShowModal(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({ necessary: true, analytics: false, marketing: false }));
    setIsVisible(false);
    setShowModal(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({ necessary: true, ...preferences }));
    setIsVisible(false);
    setShowModal(false);
  };

  // Expose function to open settings from footer
  useEffect(() => {
    // @ts-ignore
    window.openCookieSettings = () => {
      setShowModal(true);
      setIsVisible(false);
    };
  }, []);

  return (
    <>
      {/* Banner */}
      <AnimatePresence>
        {isVisible && !showModal && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] z-40 p-4 md:p-6"
          >
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-600 md:max-w-2xl">
                <p>{t('cookies.banner.text')}</p>
              </div>
              <div className="flex flex-wrap gap-3 justify-center md:justify-end">
                <button
                  onClick={() => setShowModal(true)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-[#4B2E2E] underline decoration-1 underline-offset-4 transition-colors"
                >
                  {t('cookies.banner.settings')}
                </button>
                <button
                  onClick={handleDecline}
                  className="px-6 py-2 text-sm font-medium text-[#4B2E2E] border border-[#4B2E2E] rounded-full hover:bg-[#f7f2ed] transition-colors"
                >
                  {t('cookies.banner.decline')}
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-2 text-sm font-medium text-white bg-[#4B2E2E] rounded-full hover:bg-[#3a1e1e] transition-colors shadow-md"
                >
                  {t('cookies.banner.accept')}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
                <h2 className="text-2xl font-bold text-[#4B2E2E]">{t('cookies.modal.title')}</h2>
                <p className="text-sm text-gray-600 mt-2">{t('cookies.modal.description')}</p>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Cookie Policy Details */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <h3 className="font-semibold text-[#4B2E2E] flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {t('cookies.details.title')}
                    </h3>
                    <svg 
                      className={`w-5 h-5 transition-transform ${showDetails ? 'rotate-180' : ''}`} 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {showDetails && (
                    <div className="mt-4 text-sm text-gray-700 space-y-3">
                      <p>{t('cookies.details.intro')}</p>
                      
                      <div>
                        <h4 className="font-semibold text-[#4B2E2E] mb-1">{t('cookies.details.whatAre.title')}</h4>
                        <p>{t('cookies.details.whatAre.text')}</p>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-[#4B2E2E] mb-1">{t('cookies.details.whyUse.title')}</h4>
                        <p>{t('cookies.details.whyUse.text')}</p>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-[#4B2E2E] mb-1">{t('cookies.details.types.title')}</h4>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li><strong>{t('cookies.details.types.necessary.title')}:</strong> {t('cookies.details.types.necessary.text')}</li>
                          <li><strong>{t('cookies.details.types.analytics.title')}:</strong> {t('cookies.details.types.analytics.text')}</li>
                          <li><strong>{t('cookies.details.types.marketing.title')}:</strong> {t('cookies.details.types.marketing.text')}</li>
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-[#4B2E2E] mb-1">{t('cookies.details.manage.title')}</h4>
                        <p>{t('cookies.details.manage.text')}</p>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-[#4B2E2E] mb-1">{t('cookies.details.thirdParty.title')}</h4>
                        <p>{t('cookies.details.thirdParty.text')}</p>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-[#4B2E2E] mb-1">{t('cookies.details.moreInfo.title')}</h4>
                        <p>{t('cookies.details.moreInfo.text')}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Cookie Categories */}
                <div className="space-y-3">
                  {/* Necessary */}
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-5 h-5 rounded-full bg-[#4B2E2E] flex items-center justify-center flex-shrink-0">
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <h3 className="font-semibold text-gray-900">{t('cookies.modal.necessary')}</h3>
                        </div>
                        <p className="text-sm text-gray-600 ml-8">{t('cookies.modal.necessaryDesc')}</p>
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap">{t('cookies.modal.alwaysActive')}</span>
                    </div>
                  </div>

                  {/* Analytics */}
                  <label className="block p-4 hover:bg-gray-50 rounded-lg border border-gray-200 cursor-pointer transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-2">{t('cookies.modal.analytics')}</h3>
                        <p className="text-sm text-gray-600">{t('cookies.modal.analyticsDesc')}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences(p => ({ ...p, analytics: e.target.checked }))}
                        className="w-5 h-5 text-[#4B2E2E] rounded focus:ring-[#4B2E2E] border-gray-300 mt-1 flex-shrink-0"
                      />
                    </div>
                  </label>

                  {/* Marketing */}
                  <label className="block p-4 hover:bg-gray-50 rounded-lg border border-gray-200 cursor-pointer transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-2">{t('cookies.modal.marketing')}</h3>
                        <p className="text-sm text-gray-600">{t('cookies.modal.marketingDesc')}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) => setPreferences(p => ({ ...p, marketing: e.target.checked }))}
                        className="w-5 h-5 text-[#4B2E2E] rounded focus:ring-[#4B2E2E] border-gray-300 mt-1 flex-shrink-0"
                      />
                    </div>
                  </label>
                </div>
              </div>

              <div className="p-6 bg-gray-50 border-t border-gray-100 flex flex-wrap justify-end gap-3 sticky bottom-0">
                <button
                  onClick={handleDecline}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
                >
                  {t('cookies.banner.decline')}
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-2 text-sm font-medium text-[#4B2E2E] border border-[#4B2E2E] rounded-full hover:bg-[#f7f2ed] transition-colors"
                >
                  {t('cookies.banner.accept')}
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="px-6 py-2 text-sm font-medium text-white bg-[#4B2E2E] rounded-full hover:bg-[#3a1e1e] transition-colors shadow-sm"
                >
                  {t('cookies.modal.save')}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CookieConsent;
